/**
 * Presigned S3 Chunked Upload Service
 *
 * Splits a video file into 10 MB parts and PUTs each part directly to S3
 * using time-limited presigned URLs. The NestJS API server only coordinates
 * — it never handles the actual file bytes.
 *
 * Flow:
 *   1. POST /videos/upload/initiate  → get uploadId + chunkSize + totalParts + presignedUrls[]
 *   2. PUT each part directly to its presigned S3 URL (concurrency = 6); collect ETags
 *   3. POST /videos/upload/:uploadId/complete  → server completes the S3 multipart object
 *
 * On any chunk failure the upload is aborted via DELETE /videos/upload/:uploadId/abort
 * to clean up partial S3 parts.
 */
import api from './api';

const PARALLEL_LIMIT = 6;

export interface ChunkedUploadMeta {
  title: string;
  buyerFirstName?: string;
  vin?: string;
  mimeType: string;
  fileSizeBytes: number;
}

export interface ChunkedUploadResult {
  videoId: string;
  scoringJobId: string;
}

interface UploadedPart {
  partNumber: number; // 1-indexed
  etag: string;       // as returned by S3 (with surrounding quotes)
}

/**
 * Upload a video file using the presigned-S3 chunked approach.
 *
 * @param file        The File object selected by the user.
 * @param meta        Metadata sent during initiation (title, mimeType, etc.).
 * @param onProgress  Callback receiving 0–100 progress percentage.
 *                    0–1 % during initiate, 1–99 % during part transfers, 100 % after complete.
 * @returns           videoId and scoringJobId on success.
 */
export async function uploadVideoChunked(
  file: File,
  meta: ChunkedUploadMeta,
  onProgress: (pct: number) => void,
): Promise<ChunkedUploadResult> {
  // ── Step 1: Initiate ─────────────────────────────────────────────────────
  onProgress(0);
  const initiateRes = await api.post('/videos/upload/initiate', {
    title: meta.title,
    buyerFirstName: meta.buyerFirstName,
    vin: meta.vin,
    mimeType: meta.mimeType,
    fileSizeBytes: meta.fileSizeBytes,
  });

  const { videoId, uploadId, chunkSize, totalParts, presignedUrls } = initiateRes.data
    .data as {
    videoId: string;
    uploadId: string;
    chunkSize: number;
    totalParts: number;
    presignedUrls: string[];
  };

  // ── Step 2: Direct S3 PUT per part (presigned URLs) ───────────────────
  // parts array is pre-allocated so results land in the correct index slot.
  // bytesUploaded tracks in-flight byte counts per part for smooth aggregate progress.
  const parts: UploadedPart[] = new Array(totalParts);
  const bytesUploaded = new Array<number>(totalParts).fill(0);

  // XHR is used instead of fetch because fetch does not fire upload progress events.
  // xhr.upload.onprogress fires frequently during each part's transfer, giving
  // smooth byte-level progress across all concurrently uploading parts.
  const uploadPart = (partIndex: number): Promise<void> =>
    new Promise((resolve, reject) => {
      const partNumber = partIndex + 1;
      const start = partIndex * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const blob = file.slice(start, end);

      const xhr = new XMLHttpRequest();

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          bytesUploaded[partIndex] = e.loaded;
          const totalLoaded = bytesUploaded.reduce((a, b) => a + b, 0);
          // Map byte progress to 1–99 % of the overall bar
          const pct = Math.round(1 + (totalLoaded / file.size) * 98);
          onProgress(Math.min(pct, 99));
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          // Snap this part's counter to its exact size so the aggregate
          // is accurate even if the last progress event was slightly off.
          bytesUploaded[partIndex] = blob.size;
          // S3 returns ETag with surrounding quotes, e.g. "abc123"
          // Pass it as-is; the AWS SDK CompleteMultipartUpload expects the quotes.
          const etag = xhr.getResponseHeader('ETag') ?? '';
          parts[partIndex] = { partNumber, etag };
          resolve();
        } else {
          reject(new Error(`S3 PUT failed for part ${partNumber}: HTTP ${xhr.status}`));
        }
      };

      xhr.onerror = () => reject(new Error(`Network error uploading part ${partNumber}`));
      xhr.ontimeout = () => reject(new Error(`Timeout uploading part ${partNumber}`));

      xhr.open('PUT', presignedUrls[partIndex]);
      xhr.send(blob);
    });

  // Worker-pool concurrency: spin up PARALLEL_LIMIT workers that each
  // claim the next available part index until all are done.
  let nextIndex = 0;

  const worker = async (): Promise<void> => {
    while (nextIndex < totalParts) {
      const idx = nextIndex++;
      await uploadPart(idx);
    }
  };

  try {
    await Promise.all(
      Array.from({ length: Math.min(PARALLEL_LIMIT, totalParts) }, worker),
    );
  } catch (err) {
    // Abort the S3 multipart upload to clean up partial parts
    await api.delete(`/videos/upload/${uploadId}/abort`).catch(() => undefined);
    throw err;
  }

  // ── Step 3: Complete ─────────────────────────────────────────────────────
  const completeRes = await api.post(`/videos/upload/${uploadId}/complete`, { parts });
  onProgress(100);

  const result = completeRes.data.data as {
    videoId: string;
    scoringJobId: string;
  };

  return { videoId: result.videoId, scoringJobId: result.scoringJobId };
}
